import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Upload, FileText, Image, CheckCircle, AlertTriangle,
  IndianRupee, Building2, Search, Loader2, X, Home, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';

// Format Lacs display
const formatLacs = (value) => {
  if (!value || value === 0) return '₹0 L';
  return `₹${parseFloat(value).toFixed(2)} L`;
};

// Project Search Dropdown - Shows only projects with completed site visits for the customer
const ProjectSearchDropdown = ({ value, onChange, onSelect, customerId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [eligibleProjects, setEligibleProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Fetch eligible projects when component mounts or customerId changes
  useEffect(() => {
    const fetchEligibleProjects = async () => {
      if (!customerId) {
        setEligibleProjects([]);
        setFilteredProjects([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await api.getEligibleProjectsForBooking(customerId);
        const projects = response.data?.projects || [];
        setEligibleProjects(projects);
        setFilteredProjects(projects);
        setHasLoaded(true);
        
        if (projects.length === 0) {
          toast.info('No completed site visits found. Complete a site visit first before creating a booking.', { duration: 5000 });
        }
      } catch (error) {
        console.error('Failed to fetch eligible projects:', error);
        setEligibleProjects([]);
        setFilteredProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEligibleProjects();
  }, [customerId]);

  // Filter projects based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProjects(eligibleProjects);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = eligibleProjects.filter(p => 
      p.project_name?.toLowerCase().includes(query) ||
      p.city?.toLowerCase().includes(query) ||
      p.location?.toLowerCase().includes(query)
    );
    setFilteredProjects(filtered);
  }, [searchQuery, eligibleProjects]);

  const handleSelect = (project) => {
    setSelectedProject(project);
    onSelect(project);
    setIsOpen(false);
    setSearchQuery('');
  };

  // No customer linked - show disabled state
  if (!customerId) {
    return (
      <div className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-sm">
        <Building2 size={16} className="inline mr-2" />
        Link to a CRM customer first
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        onClick={() => eligibleProjects.length > 0 && setIsOpen(true)}
        className={`w-full px-3 py-2.5 rounded-xl border bg-white flex items-center gap-2
          ${eligibleProjects.length === 0 ? 'border-amber-300 bg-amber-50 cursor-not-allowed' : 
            isOpen ? 'border-[#EF4444] ring-2 ring-red-100 cursor-pointer' : 'border-slate-200 hover:border-slate-300 cursor-pointer'}`}
      >
        <Building2 size={16} className={eligibleProjects.length === 0 ? 'text-amber-500' : 'text-slate-400'} />
        {selectedProject ? (
          <span className="text-sm text-slate-800">{selectedProject.project_name}</span>
        ) : loading ? (
          <span className="text-sm text-slate-400 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading eligible projects...
          </span>
        ) : eligibleProjects.length === 0 ? (
          <span className="text-sm text-amber-600">No projects with completed visits</span>
        ) : (
          <span className="text-sm text-slate-400">Select from {eligibleProjects.length} eligible project(s)...</span>
        )}
      </div>
      
      {/* Show message when no eligible projects */}
      {hasLoaded && eligibleProjects.length === 0 && (
        <p className="text-xs text-amber-600 mt-1">
          Complete a site visit for this customer first to create a booking.
        </p>
      )}
      
      {/* Show count when projects available */}
      {eligibleProjects.length > 0 && !selectedProject && (
        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
          <CheckCircle size={12} />
          {eligibleProjects.length} project(s) with completed site visits
        </p>
      )}
      
      {isOpen && eligibleProjects.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter projects..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#EF4444]"
              />
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {filteredProjects.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No matching projects
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.project_id}
                  onClick={() => handleSelect(project)}
                  className="px-3 py-2 hover:bg-emerald-50 cursor-pointer border-b border-slate-50 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-emerald-500" />
                    <p className="text-sm font-medium text-slate-800">{project.project_name}</p>
                  </div>
                  <p className="text-xs text-slate-500 ml-6">{project.location || ''}{project.location && project.city ? ', ' : ''}{project.city || ''}</p>
                </div>
              ))
            )}
          </div>
          <div className="p-2 border-t border-slate-100 bg-slate-50">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-xs text-slate-500 hover:text-slate-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// File Upload Component
const FileUpload = ({ label, accept, value, onChange, docType, optional = false }) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.uploadBookingDocument(formData, docType);
      onChange(response.data.url);
      setFileName(file.name);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  // Get appropriate icon based on docType
  const getIcon = () => {
    switch (docType) {
      case 'booking_form':
        return <FileText size={24} className="text-slate-400 mb-2" />;
      case 'cost_sheet':
        return <FileSpreadsheet size={24} className="text-slate-400 mb-2" />;
      default:
        return <Image size={24} className="text-slate-400 mb-2" />;
    }
  };

  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">
        {label} {optional ? <span className="text-slate-400">(Optional)</span> : '*'}
      </Label>
      <div className="mt-1">
        {value ? (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <CheckCircle size={18} className="text-emerald-600" />
            <span className="text-sm text-emerald-700 flex-1 truncate">{fileName || 'File uploaded'}</span>
            <button
              type="button"
              onClick={() => { onChange(''); setFileName(''); }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className={`flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-xl cursor-pointer transition-colors
            ${uploading ? 'border-slate-300 bg-slate-50' : 'border-slate-200 hover:border-[#EF4444] hover:bg-red-50'}`}>
            {uploading ? (
              <Loader2 size={24} className="animate-spin text-slate-400 mb-2" />
            ) : (
              getIcon()
            )}
            <span className="text-sm text-slate-500">{uploading ? 'Uploading...' : 'Click to upload'}</span>
            <span className="text-xs text-slate-400 mt-1">
              {docType === 'cost_sheet' ? 'PDF, JPG, PNG, Excel (max 10MB)' : 'PDF, JPG, PNG (max 10MB)'}
            </span>
            <input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
      </div>
    </div>
  );
};

const CreateBooking = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Get customer info from URL params (when coming from customer detail page)
  const customerIdFromUrl = searchParams.get('customer_id');
  const customerEmailFromUrl = searchParams.get('customer_email');
  const customerNameFromUrl = searchParams.get('customer_name');
  
  const [formData, setFormData] = useState({
    project_id: '',
    project_name: '',
    unit_no: '',
    agreement_value_lacs: '',
    token_amount_lacs: '',
    customer_email: customerEmailFromUrl || '',
    customer_name: customerNameFromUrl || '',
    customer_id: customerIdFromUrl || '',
    notes: '',
    booking_form_url: '',
    payment_proof_url: '',
    cost_sheet_url: '',
    booking_date: new Date().toISOString().split('T')[0]  // Default to today
  });

  // Calculate token percentage
  const tokenPercent = formData.agreement_value_lacs && formData.token_amount_lacs
    ? ((parseFloat(formData.token_amount_lacs) / parseFloat(formData.agreement_value_lacs)) * 100).toFixed(2)
    : 0;

  const isTokenLow = parseFloat(tokenPercent) < 1 && parseFloat(tokenPercent) > 0;

  const handleProjectSelect = (project) => {
    setFormData(prev => ({
      ...prev,
      project_id: project.project_id,
      project_name: project.project_name
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.unit_no.trim()) {
      toast.error('Unit number is required');
      return;
    }
    if (!formData.agreement_value_lacs || parseFloat(formData.agreement_value_lacs) <= 0) {
      toast.error('Agreement Value must be greater than 0');
      return;
    }
    if (!formData.token_amount_lacs || parseFloat(formData.token_amount_lacs) <= 0) {
      toast.error('Token Amount must be greater than 0');
      return;
    }
    if (!formData.customer_email) {
      toast.error('Customer email is required');
      return;
    }
    if (!formData.booking_form_url) {
      toast.error('Please upload the booking form');
      return;
    }
    if (!formData.payment_proof_url) {
      toast.error('Please upload the payment proof');
      return;
    }

    setLoading(true);
    try {
      const response = await api.createVerifiedBooking({
        project_id: formData.project_id,
        unit_no: formData.unit_no.trim(),
        agreement_value_lacs: parseFloat(formData.agreement_value_lacs),
        token_amount_lacs: parseFloat(formData.token_amount_lacs),
        customer_id: formData.customer_id || null,  // Include customer_id for CRM linking
        customer_email: formData.customer_email.trim(),
        customer_name: formData.customer_name.trim() || null,
        notes: formData.notes.trim() || null,
        booking_form_url: formData.booking_form_url,
        payment_proof_url: formData.payment_proof_url,
        cost_sheet_url: formData.cost_sheet_url || null,
        booking_date: formData.booking_date  // Actual booking date
      });

      const result = response.data;
      
      // Show appropriate message based on verification status and financial flag
      if (result.financial_flag === 'LOW_TOKEN_ALERT' || result.verification_status === 'TOKEN_BLOCK') {
        toast.warning(`Booking submitted with Low Token Alert (Token ${result.token_percent}% < 1%). It will be reviewed by City Head.`);
      } else if (result.verification_status === 'NEEDS_REVIEW') {
        toast.info('Booking submitted for review: ' + result.verification_flags.join(', '));
      } else {
        toast.success('Booking submitted and auto-verified!');
      }
      
      navigate('/my-bookings');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit booking';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button 
            onClick={() => navigate('/')} 
            className="p-2 hover:bg-slate-100 rounded-xl"
            data-testid="home-btn"
          >
            <Home size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-[#111827]">New Booking</h1>
            <p className="text-xs text-slate-500">Submit for verification</p>
          </div>
          <MobileMenuTrigger user={user} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4 max-w-lg mx-auto">
        
        {/* Project Selection */}
        <Card className="p-4 bg-white shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={18} className="text-[#5B3DF5]" />
            <h2 className="font-bold text-[#111827]">Project Details</h2>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Booking Date *</Label>
              <Input
                type="date"
                value={formData.booking_date}
                onChange={(e) => setFormData(prev => ({ ...prev, booking_date: e.target.value }))}
                className="mt-1 h-11 rounded-xl"
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-slate-400 mt-1">When was the booking made? (Cannot be future date)</p>
            </div>
            
            <div>
              <Label className="text-xs font-semibold text-slate-600">Project *</Label>
              <div className="mt-1">
                <ProjectSearchDropdown
                  value={formData.project_id}
                  onChange={(id) => setFormData(prev => ({ ...prev, project_id: id }))}
                  onSelect={handleProjectSelect}
                  customerId={formData.customer_id}
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs font-semibold text-slate-600">Unit No *</Label>
              <Input
                value={formData.unit_no}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_no: e.target.value }))}
                placeholder="e.g., A-1205"
                className="mt-1 h-11 rounded-xl"
              />
            </div>
          </div>
        </Card>

        {/* Financial Details */}
        <Card className="p-4 bg-white shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 mb-3">
            <IndianRupee size={18} className="text-[#5B3DF5]" />
            <h2 className="font-bold text-[#111827]">Financial Details</h2>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Agreement Value (AV) *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.agreement_value_lacs}
                  onChange={(e) => setFormData(prev => ({ ...prev, agreement_value_lacs: e.target.value }))}
                  placeholder="126.37"
                  className="pl-7 pr-12 h-11 rounded-xl"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Lacs</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">Enter value in Lacs (e.g., 126.37 = ₹1,26,37,000)</p>
            </div>
            
            <div>
              <Label className="text-xs font-semibold text-slate-600">Token Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.token_amount_lacs}
                  onChange={(e) => setFormData(prev => ({ ...prev, token_amount_lacs: e.target.value }))}
                  placeholder="5.00"
                  className="pl-7 pr-12 h-11 rounded-xl"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Lacs</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">Enter value in Lacs (e.g., 5.00 = ₹5,00,000)</p>
            </div>
            
            {/* Token Percentage Display */}
            {formData.agreement_value_lacs && formData.token_amount_lacs && (
              <div className={`p-3 rounded-xl ${isTokenLow ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Token %:</span>
                  <span className={`font-bold ${isTokenLow ? 'text-[#5B3DF5]' : 'text-emerald-600'}`}>
                    {tokenPercent}%
                  </span>
                </div>
                {isTokenLow && (
                  <div className="flex items-center gap-1 mt-2 text-amber-600">
                    <AlertTriangle size={14} />
                    <span className="text-xs font-medium">Token below 1% – Low Token Alert will be flagged for review</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Customer Details */}
        <Card className="p-4 bg-white shadow-sm rounded-2xl">
          <h2 className="font-bold text-[#111827] mb-3">Customer Details</h2>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Customer Email *</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="customer@email.com"
                className="mt-1 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold text-slate-600">Customer Name <span className="text-slate-400">(Optional)</span></Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="Customer name"
                className="mt-1 h-11 rounded-xl"
              />
            </div>
          </div>
        </Card>

        {/* Document Uploads */}
        <Card className="p-4 bg-white shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={18} className="text-[#5B3DF5]" />
            <h2 className="font-bold text-[#111827]">Documents</h2>
          </div>
          
          <div className="space-y-4">
            <FileUpload
              label="Booking Form"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              value={formData.booking_form_url}
              onChange={(url) => setFormData(prev => ({ ...prev, booking_form_url: url }))}
              docType="booking_form"
            />
            
            <FileUpload
              label="Payment Proof"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              value={formData.payment_proof_url}
              onChange={(url) => setFormData(prev => ({ ...prev, payment_proof_url: url }))}
              docType="payment_proof"
            />
            
            <FileUpload
              label="Cost Sheet"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls"
              value={formData.cost_sheet_url}
              onChange={(url) => setFormData(prev => ({ ...prev, cost_sheet_url: url }))}
              docType="cost_sheet"
              optional={true}
            />
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-4 bg-white shadow-sm rounded-2xl">
          <Label className="text-xs font-semibold text-slate-600">Notes <span className="text-slate-400">(Optional)</span></Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Any additional notes..."
            rows={3}
            className="mt-1 rounded-xl"
          />
        </Card>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#5B3DF5] hover:bg-[#4A2FE0] text-white font-semibold rounded-xl shadow-lg"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            'Submit Booking'
          )}
        </Button>
      </form>
    </div>
  );
};

export default CreateBooking;
